import type { RevisionRequestPart, WikiRevisionRequest } from "@esi/shared";
import { Bot, MessageSquareWarning } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDateTime } from "../i18n/format.ts";
import { useOpenWiki } from "../router.tsx";

/**
 * A Revision requested card (wiki-collaboration D15): a reviewer asked the agent to revise an
 * item of this conversation's change set. Its creator sends the turn with one click (Revise); no
 * turn runs without it. The suggestion-card anatomy (DESIGN.md "Suggestion cards"): a dashed
 * box, a muted icon, the 13px title line, the request in 14px, the 12px facts, small actions.
 */
export function RevisionRequestCard(props: {
  request: WikiRevisionRequest;
  /** Sending a turn now is possible (no turn running, the composer is the creator's). */
  canRevise: boolean;
  onRevise: (request: WikiRevisionRequest) => void;
}) {
  const { request, canRevise, onRevise } = props;
  const { t } = useTranslation();
  const id = useId();
  const { show } = useOpenWiki();
  const name = request.requestedByName ?? t("wiki.revision.someone");
  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
        "transition-colors duration-(--duration-fast) ease-(--ease-out) focus-within:border-solid",
      )}
    >
      <div className="flex items-start gap-2.5">
        <MessageSquareWarning
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {t("wiki.revision.title")}
          </h3>
          <p className="break-words text-sm font-medium text-fg">
            {t("wiki.revision.by", { name, item: request.itemTitle })}
          </p>
          {request.quote ? (
            // The passage asked about (wiki-collaboration-ux 12.7): the item's own text.
            <blockquote
              data-esi-content="wiki"
              className="max-w-prose border-l-2 border-line-strong pl-2.5 text-[13px] break-words text-fg-secondary"
            >
              <span className="sr-only">{t("wiki.revision.passage")} </span>“{request.quote}”
            </blockquote>
          ) : null}
          <p
            data-esi-content="wiki"
            className="max-w-prose whitespace-pre-wrap break-words text-sm text-fg-secondary"
          >
            {request.comment}
          </p>
          <p className="text-xs text-fg-muted">
            <time dateTime={request.requestedAt}>{formatDateTime(request.requestedAt)}</time>
          </p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
        <Button type="button" size="sm" disabled={!canRevise} onClick={() => onRevise(request)}>
          <Bot aria-hidden="true" strokeWidth={1.75} />
          {t("wiki.revision.revise")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="text-fg-secondary"
          onClick={() =>
            show({ kind: "changes", changeSetId: request.changeSetId, itemId: request.itemId })
          }
        >
          {t("wiki.revision.openReview")}
        </Button>
      </div>
    </section>
  );
}

/** A sent Revise turn's user message, settled into one quiet status line (never the raw text). */
export function RevisionSentLine({ data }: { data: RevisionRequestPart["data"] }) {
  const { t } = useTranslation();
  const { show } = useOpenWiki();
  return (
    <div className="flex flex-col items-end gap-1 self-end">
      {data.quote ? (
        // The passage the Revise turn is about (wiki-collaboration-ux 12.7).
        <blockquote
          data-esi-content="wiki"
          className="max-w-prose border-r-2 border-line-strong pr-2.5 text-right text-xs break-words text-fg-secondary"
        >
          <span className="sr-only">{t("wiki.revision.passage")} </span>“{data.quote}”
        </blockquote>
      ) : null}
      <p className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs text-fg-muted">
        <Bot aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>
          {t("wiki.revision.settled", {
            item: data.itemTitle,
            name: data.requestedByName ?? t("wiki.revision.someone"),
          })}
        </span>
        <button
          type="button"
          className="text-accent-text underline-offset-4 hover:underline"
          onClick={() =>
            show({ kind: "changes", changeSetId: data.changeSetId, itemId: data.itemId })
          }
        >
          {t("wiki.revision.openReview")}
        </button>
      </p>
    </div>
  );
}
